/**
 * Pure wording for the "Quality is waiting on you" rows (kanban 2a28d4c0).
 * Logic lives here, the card in QualityAsksCard.tsx (repo convention).
 */
import type { MyAsk } from '../../../services/portalQualityAsks';
import { formatShortDate } from '../../../utils/dateFormatters';

const BODY_MAX = 220;

/** "A&H Concrete (AHCON01)", the number alone when the name is unknown. */
export function askCustomerLabel(ask: MyAsk): string | null {
  if (!ask.customer) return null;
  const { name, customer_no } = ask.customer;
  return name ? `${name} (${customer_no})` : customer_no;
}

/** "case INV-00005 · QT-10026", "QT-10026", or null for a chat-born ask. */
export function askReference(ask: MyAsk): string | null {
  const parts: string[] = [];
  if (ask.case) parts.push(`case ${ask.case.investigation_no}`);
  if (ask.thread) parts.push(ask.thread.thread_no);
  return parts.length ? parts.join(' · ') : null;
}

/** "asked Oct 7 by Merlin for Jess Finlaw (quality)" */
export function askedLine(ask: MyAsk): string {
  return `asked ${formatShortDate(ask.asked_at)} by ${ask.via} for ${ask.asked_for} (quality)`;
}

/** The question on one line: markdown bullets and line breaks folded, capped.
 *  A line that ends without punctuation gets a period before the next one,
 *  so a bullet does not run into the sign-off ("…so the record shows it A
 *  reply here reaches Merlin and me", staging E2E 2026-10-09). */
export function askOneLine(body: string): string {
  const lines = body
    .split('\n')
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
    .filter(Boolean);
  const flat = lines
    .map((l, i) => (i < lines.length - 1 && !/[.!?:;,…]$/.test(l) ? `${l}.` : l))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return flat.length > BODY_MAX ? `${flat.slice(0, BODY_MAX - 1).trimEnd()}…` : flat;
}

/** Where "Open the item" goes on the quality door: the case when there is
 *  one, else the thread; null for an ask on neither. */
export function askItemPath(ask: MyAsk): string | null {
  if (ask.case) return `/portal/quality-copilot/cases/${encodeURIComponent(ask.case.investigation_no)}`;
  if (ask.thread) return `/portal/quality-copilot/threads/${encodeURIComponent(ask.thread.thread_no)}`;
  return null;
}

/** The ids an audit row carries for an ask. */
export function askTrackIds(ask: MyAsk): Record<string, string | null> {
  return {
    ask_id: ask.id,
    investigation_no: ask.case?.investigation_no ?? null,
    thread_no: ask.thread?.thread_no ?? null,
    customer_no: ask.customer?.customer_no ?? null,
  };
}
